import type { ContractPreferencesCurrentStep as CurrentStep } from 'src/types';

import { paths } from 'src/routes/paths';
import { useParams, useRouter, useSearchParams } from 'src/routes/hooks';

import { useContractSetupStore } from 'src/store/contractSetupStore';
import {
  type StoreMode,
  usePreferencesStore,
  type PreferencesStep,
} from 'src/store/preferencesStore';

// Flow maps differ by mode: `start` exists only in contract mode
// No requirements/commitments: only reached in edit mode (Exit, not Previous Step) and (Save changes, not Next Step).
const contractFlow: Partial<
  Record<CurrentStep | 'start', { next?: CurrentStep | 'success'; prev?: CurrentStep | 'start' }>
> = {
  start: { next: 'geography' },
  geography: { next: 'themes', prev: 'start' },
  themes: { prev: 'geography', next: 'impact' },
  impact: { prev: 'themes', next: 'scoring' },
  scoring: { prev: 'impact', next: 'qualitative' },
  qualitative: { prev: 'scoring', next: 'finalizing' },
  finalizing: { prev: 'qualitative', next: 'success' },
};

const existingContractFlow: Partial<
  Record<CurrentStep | 'start', { next?: CurrentStep | 'success'; prev?: CurrentStep | 'start' }>
> = {
  start: { next: 'geography' },
  geography: { next: 'themes', prev: 'start' },
  themes: { prev: 'geography', next: 'impact' },
  impact: { prev: 'themes', next: 'scoring' },
  scoring: { prev: 'impact', next: 'requirements' },
  // requirements before commitments: that step writes the reporting cadence, and committing to
  // activities needs the rrule to already exist (getAwardedContractApplication requires it).
  requirements: { prev: 'scoring', next: 'commitments' },
  commitments: { prev: 'requirements', next: 'previous-delivery' },
  // #634: previous-delivery after requirements, because that step writes the reporting cadence and
  // the cut-off has to land on one of its boundaries. Chosen before it, the contract still has no
  // rrule, so a quarterly contract is offered month ends and the supplier's first quarter then
  // overlaps the backfilled months — the same delivery counted on both sides of the handover.
  'previous-delivery': { prev: 'commitments', next: 'success' },
};

const preferencesFlow: Partial<
  Record<CurrentStep | 'success', { next?: CurrentStep | 'success'; prev?: CurrentStep }>
> = {
  geography: { next: 'themes' },
  themes: { prev: 'geography', next: 'impact' },
  impact: { prev: 'themes', next: 'scoring' },
  scoring: { prev: 'impact', next: 'qualitative' },
  qualitative: { prev: 'scoring', next: 'finalizing' },
  finalizing: { prev: 'qualitative', next: 'success' },
};

const redirectToStep = (
  baseUrl: string,
  currentStep: CurrentStep | 'start',
  direction: 'next' | 'prev',
  flow: Partial<
    Record<CurrentStep | 'start', { next?: CurrentStep | 'success'; prev?: CurrentStep | 'start' }>
  >
) => {
  const currentFlow = flow[currentStep];
  if (!currentFlow) {
    throw new Error(`Current step ${currentStep} not found in flow`);
  }
  const target = currentFlow[direction];
  if (!target) {
    throw new Error(`Target step ${target} not found in flow`);
  }
  return target ? `${baseUrl}/${target}` : '';
};

export const useSetupNavigation = (mode: StoreMode) => {
  const router = useRouter();
  const params = useParams();
  const baseUrl =
    mode === 'contract' ? `/procurer/contract/${params.contractId}` : `/procurer/preferences`;
  const [searchParams] = useSearchParams();
  const isEdit = searchParams.get('edit') === 'true';
  const fromParam = searchParams.get('from');
  const { setOpenSaveSuccessModal } = usePreferencesStore();
  const contractMetadataStore = useContractSetupStore((s) => s.contractMetadata);
  const isExistingContract = contractMetadataStore.isNew === 'no';

  const flow =
    mode === 'contract'
      ? isExistingContract
        ? existingContractFlow
        : contractFlow
      : preferencesFlow;

  const editQueryString = fromParam ? `?edit=true&from=${fromParam}` : '?edit=true';

  const navigateToStep = (step: PreferencesStep) =>
    router.push(isEdit ? `${baseUrl}/${step}${editQueryString}` : `${baseUrl}/${step}`);

  const navigateToNextStep = (currentStep: CurrentStep | 'start') => {
    if (isEdit) {
      if (setOpenSaveSuccessModal) {
        setOpenSaveSuccessModal(true);
      }
      return;
    }
    router.push(redirectToStep(baseUrl, currentStep, 'next', flow));
  };

  const navigateToPrevStep = (currentStep: CurrentStep | 'start') => {
    router.push(redirectToStep(baseUrl, currentStep, 'prev', flow));
  };

  const exitCtaFn = () => {
    if (mode === 'contract' && isEdit && params.contractId) {
      if (fromParam === 'open-for-tender') {
        router.push(paths.dashboard.getSingleContractOpenForTender(params.contractId));
      } else {
        router.push(paths.dashboard.getSingleContractPath(params.contractId));
      }
    } else {
      router.push(paths.dashboard.contracts);
    }
  };

  return {
    navigateToStep,
    navigateToNextStep,
    navigateToPrevStep,
    isEdit,
    exitCtaFn,
  };
};
